import { z } from 'zod';
import { audienceSpecSchema } from '../audience/index';

/**
 * Contracten van de module shortcuts: tegels met een link naar een ander systeem (Stap 19).
 *
 * De gatenanalyse noemt dit "de meest onderschatte functie voor deze markt", en dat klopt om een
 * onopvallende reden: een medewerker zonder bedrijfse-mail en zonder pc heeft wél een loonbrief, een
 * verlofsaldo en een uurregistratie — alleen staan die in drie systemen met elk een eigen
 * aanmelding. Eén tegel die hem met zijn personeelsnummer op de juiste pagina zet, is voor hem meer
 * waard dan de helft van de rest van dit platform.
 *
 * Twee soorten tegels, en het verschil zit in de URL:
 *
 * - `fixed`: dezelfde link voor iedereen. "Het arbeidsreglement."
 * - `dynamic`: een link met `{veldkey}` erin, die de server per medewerker invult met zijn eigen
 *   gegevensveld. "Mijn loonbrief" wordt `…/loonbrief?pnr=4711`.
 */

export const shortcutKindSchema = z.enum(['fixed', 'dynamic']);
export type ShortcutKind = z.infer<typeof shortcutKindSchema>;

/**
 * Een handvol iconen uit de bibliotheek, en geen eigen uploads in deze slice.
 *
 * Een geüpload icoon moet door `media`, krijgt een presigned URL per tegel en moet op een
 * tegelraster van 48 px nog leesbaar zijn — drie problemen voor iets wat een vaste set ook oplost.
 */
export const shortcutIconSchema = z.enum([
  'payslip',
  'calendar',
  'clock',
  'document',
  'euro',
  'health',
  'link',
  'tools',
]);
export type ShortcutIcon = z.infer<typeof shortcutIconSchema>;

/**
 * De kleur van het tegeltje, als rol en niet als hexcode.
 *
 * Dit is een veld op de tegel en niet iets dat uit het icoon volgt, en dat is een bewuste
 * ommezwaai. Een snelkoppeling wijst naar een **ander systeem** — een loonverwerker, een
 * tijdsregistratie, een intranet, een helpdesk — en zulke bestemmingen passen zelden in een van de
 * acht iconen. Het merendeel krijgt `link`, en kleur-uit-icoon levert dan een rooster van
 * identieke grijze tegels op.
 *
 * Met de kleur op de tegel kan ze bovendien iets betekenen: rood voor een ongevalmelding, groen
 * voor de loonbrief. Op een telefoon met handschoenen aan is dat het verschil tussen zoeken en
 * aanwijzen — en de kleur staat nooit alleen, er staat altijd een label onder (a11y).
 *
 * Welke kleur `danger` precies is, staat in `packages/ui`. Daarom rollen in de database en geen
 * hexcodes: een klant die een andere huisstijl krijgt, hoeft geen rijen te wijzigen.
 */
export const shortcutColorSchema = z.enum([
  'neutral',
  'primary',
  'cyan',
  'dark',
  'orange',
  'success',
  'warning',
  'danger',
]);
export type ShortcutColor = z.infer<typeof shortcutColorSchema>;

/** Eén tegel zoals de medewerker hem ziet. De URL staat er níet in — zie `shortcutDestinationSchema`. */
export const myShortcutSchema = z.object({
  id: z.guid(),
  label: z.string(),
  icon: shortcutIconSchema,
  color: shortcutColorSchema,
  /** Een ondertekend adres van het eigen icoon (FBK-8), of `null` voor het bibliotheekicoon. */
  iconUrl: z.string().nullable().default(null),
  showOnHome: z.boolean(),
});
export type MyShortcut = z.infer<typeof myShortcutSchema>;

export const myShortcutListSchema = z.object({ items: z.array(myShortcutSchema) });
export type MyShortcutList = z.infer<typeof myShortcutListSchema>;

/**
 * De bestemming van één tegel, opgevraagd op het moment van tikken.
 *
 * **Waarom niet een `302` van de server?** Dat was de eerste opzet, en ze werkte niet: een gewone
 * `<a href>` naar deze API is een navigatie van de browser, en die stuurt geen `Authorization`-header
 * mee — het toegangstoken leeft in het geheugen van de app, niet in een cookie. Elke tik gaf dus
 * een `401`.
 *
 * Nu vraagt de client de bestemming op met zijn token, de server telt de klik en geeft de URL
 * terug, en de client navigeert. De URL staat daarmee nog steeds niet in de HTML van de pagina:
 * hij wordt pas bij het tikken opgehaald.
 */
export const shortcutDestinationSchema = z.object({ url: z.string() });
export type ShortcutDestination = z.infer<typeof shortcutDestinationSchema>;

export const shortcutSchema = z.object({
  id: z.guid(),
  label: z.string(),
  icon: shortcutIconSchema,
  color: shortcutColorSchema,
  /** Het eigen icoon (FBK-8): het bestand, en een ondertekend adres om het te tonen. */
  iconFileId: z.guid().nullable().default(null),
  iconUrl: z.string().nullable().default(null),
  kind: shortcutKindSchema,
  urlTemplate: z.string(),
  audience: audienceSpecSchema,
  audienceDescription: z.string(),
  showOnHome: z.boolean(),
  position: z.number().int(),
  clickCount: z.number().int(),
  /** Hoeveel verschillende mensen erop tikten. Aantallen, nooit namen (ADR-0014). */
  uniqueClickers: z.number().int(),
  createdAt: z.string().datetime(),
});
export type Shortcut = z.infer<typeof shortcutSchema>;

export const shortcutListSchema = z.object({ items: z.array(shortcutSchema) });
export type ShortcutList = z.infer<typeof shortcutListSchema>;

export const createShortcutRequestSchema = z.object({
  label: z.string().trim().min(1, 'Geef de tegel een naam.').max(60),
  icon: shortcutIconSchema,
  color: shortcutColorSchema.default('neutral'),
  /** Een eigen icoon, opgeladen met doel `shortcut_icon` (FBK-8). */
  iconFileId: z.guid().nullable().optional(),
  kind: shortcutKindSchema,
  /**
   * Alleen `https`, en alleen naar een domein dat voor dit bedrijf is toegestaan.
   *
   * Dat laatste wordt op de server gecontroleerd en niet hier: de lijst met toegestane domeinen
   * staat per bedrijf in de database, en een zod-schema kent geen tenant.
   */
  urlTemplate: z.string().trim().url().max(2000),
  audience: audienceSpecSchema,
  showOnHome: z.boolean().default(true),
});
export type CreateShortcutRequest = z.infer<typeof createShortcutRequestSchema>;

/**
 * Een tegel wijzigen. Elk veld is optioneel; wat je weglaat, blijft staan.
 *
 * Tot UI-5 bestond deze weg niet: een tegel kon alleen aangemaakt en verwijderd worden. Een kleur
 * of een typefout herstellen betekende dus de tegel opnieuw maken, en dan begonnen de tellers op
 * nul — de enige cijfers die zeggen of een tegel gebruikt wordt. Dat is de reden dat dit er nu is,
 * en niet de wens om alles te kunnen bewerken.
 *
 * De server toetst de **uitkomst** en niet de wijziging. Wie alleen `kind` op `fixed` zet terwijl
 * er een `{personeelsnummer}` in het webadres staat, maakt een tegel die voor niemand werkt; dat
 * is pas te zien als je het bestaande adres erbij neemt.
 */
/**
 * De volgorde van de tegels (Extra 6, punt 10).
 *
 * De volledige lijst in één aanroep en geen "verplaats tegel X naar plek 3": bij twee mensen die
 * tegelijk slepen, zou dat laatste twee halve volgordes opleveren. Zo wint er één, en de ander
 * ziet bij zijn volgende handeling wat er staat.
 */
export const reorderShortcutsRequestSchema = z.object({
  shortcutIds: z.array(z.guid()).min(1).max(100),
});
export type ReorderShortcutsRequest = z.infer<typeof reorderShortcutsRequestSchema>;

export const updateShortcutRequestSchema = z
  .object({
    label: z.string().trim().min(1, 'Geef de tegel een naam.').max(60),
    icon: shortcutIconSchema,
    color: shortcutColorSchema,
    /** `null` haalt het eigen icoon weg. */
    iconFileId: z.guid().nullable(),
    kind: shortcutKindSchema,
    urlTemplate: z.string().trim().url().max(2000),
    audience: audienceSpecSchema,
    showOnHome: z.boolean(),
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: 'Er is niets te wijzigen.' });
export type UpdateShortcutRequest = z.infer<typeof updateShortcutRequestSchema>;

/**
 * Het voorbeeld voor de beheerder: wat wordt deze URL voor één medewerker?
 *
 * Nodig omdat een dynamische tegel stil kan mislukken: ontbreekt het veld bij een medewerker, dan
 * ziet die de tegel niet, en dat merkt de beheerder anders nooit.
 */
export const shortcutPreviewSchema = z.object({
  /** De ingevulde URL, of leeg als er een veld ontbreekt. */
  url: z.string().nullable(),
  /** De sleutels die niet gevuld konden worden. */
  missingFields: z.array(z.string()),
  /** Of deze medewerker de tegel te zien krijgt. */
  visible: z.boolean(),
});
export type ShortcutPreview = z.infer<typeof shortcutPreviewSchema>;

export const allowedDomainSchema = z.object({
  id: z.guid(),
  domain: z.string(),
});
export type AllowedDomain = z.infer<typeof allowedDomainSchema>;

export const allowedDomainListSchema = z.object({ items: z.array(allowedDomainSchema) });
export type AllowedDomainList = z.infer<typeof allowedDomainListSchema>;

export const addAllowedDomainRequestSchema = z.object({
  /** Alleen de hostnaam: "loon.example.com". Geen schema, geen pad, geen jokertekens. */
  domain: z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(253)
    .regex(
      /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/,
      'Geef alleen een hostnaam, bijvoorbeeld loon.example.com.',
    ),
});
export type AddAllowedDomainRequest = z.infer<typeof addAllowedDomainRequestSchema>;
